import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    AccessCodeProps,
    deleteUserAppointment,
    getUserAppointments,
    openAmazonDoor,
    outSideGeoLocationAlert,
    requestAccessCode,
    resetAmazonDoor,
} from '../../redux/appointment/actions/appointment.action';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import Popup from '../common/popup/Popup';
import { AppointmentsProps } from './appointments.interface';
import DeleteAppointmentPopup from './DeleteAppointmentPopup';
import TourBookingMessage from '../locations/PropertyMessage/TourBookingMessage';
import Webcam from 'react-webcam';
import Toast from '../toast/Toast';
import SuccessAnimation from '../icons/SuccessIcon/SuccessAnimation';
import iconSort from '../../images/icons/icon-sortby.svg';
import CloseIcon from '../../images/icon-close.svg';
import CamLayer from '../../images/cam-layer.svg';
import Loader from '../common/loader/Loader';
import styles from './Appointments.module.css';

import useWindowDimension from '../../utils/useWindowdimension';
import AppointmentCard from './AppointmentCard/AppointmentCard';
import OpenViaIcon from '../icons/OpenViaIcon';
import MapIcon from '../icons/MapIcon';
import TrashIcon from '../icons/TrashIcon';
import EditIcon from '../icons/EditIcon';
import NoAppointments from '../../images/noPropertyImg.svg';
import { useGeolocation } from '../../hooks/geoLocation';
import { AmazoneFlag } from '../../utils/constants/constants';
import { queryAllByPlaceholderText } from '@testing-library/dom';
import { useLayoutEffect } from 'react';

const colors: string[] = ['#F6F6F6', '#FDF8F1', '#F4F7FC', '#F2FBF8', '#FCF7F8'];

export const isAccessible = (appointmentDate: string, appointmentStartTime: string, appointmentEndTime: string) => {
    const startDate = new Date(`${appointmentDate} ${appointmentStartTime}`);
    const endDate = new Date(`${appointmentDate} ${appointmentEndTime}`);
    const currentDate = new Date();

    const startTimeRange = new Date(startDate.getTime() - 15 * 60000);
    const endTimeRange = new Date(endDate.getTime() + 45 * 60000);

    if (currentDate < startTimeRange) {
        return 0;
    } else if (currentDate >= startTimeRange && currentDate <= endTimeRange) {
        return 1;
    } else {
        return -1;
    }
};

export interface amazonDoorProps {
    locationId: string;
    appointmentId?: string;
    vendorId?: string;
    latitude: number;
    longitude: number;
    timestamp: number;
    amazonFlag?: string;
}

export type ToastType = 'success' | 'error' | 'warning';
type AppointmentQueryAction = 'reschedule' | 'cancel' | '';

const Appointments = () => {
    const [searchParams] = useSearchParams();
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const windowWidth = useWindowDimension();


    const [open, setOpen] = useState<boolean>(false);
    const [showBookingInfo, setShowBookingInfo] = useState<boolean>(false);
    const [id, setId] = useState<string>('');
    const [active, setActive] = useState<string>('Desc');
    const [appointmentId, setAppointmentId] = useState<string>('');
    const [appointmentAction, setAppointmentAction] = useState<AppointmentQueryAction>('');
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState<ToastType>('error');
    const [selectedAppointment, setSelectedAppointment] = useState({
        appointmentId: '',
        count: 0,
    });
    const [accessCode, setAccessCode] = useState('');
    const [showModalPopup, setShowModalPopup] = useState(false);

    const [showSwipeModal, setShowSwipeModal] = useState(false);
    const [selectedTour, setSelectedTour] = useState<AppointmentsProps | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [appointmentData, setAppointmentData] = useState<AppointmentsProps[] | null>(null);

    const appointments: AppointmentsProps[] = useSelector((state: IRootState) => state.appointmentReducer.appointments.data);
    const loading: boolean = useSelector((state: IRootState) => state.appointmentReducer.appointments.loading);
    const {
        loading: doorOpenloading,
        data: doorOpenData,
        error: doorOpenError,
    } = useSelector((state: IRootState) => state.appointmentReducer.amazonDoor);
    const [showCustomModal, setShowCustomModal] = useState(false);
    const { coordinates, isEnabled, askForPermissionIfNeeded, error: geoLocationError, isSupported } = useGeolocation();

    const storeAppointments = async (appointments: AppointmentsProps[] | null) => {
        if (appointments) {
            const currentAppointments: AppointmentsProps[] = [];
            appointments?.forEach((appointment: AppointmentsProps) => {
                if (
                    isAccessible(appointment?.appointmentDate, appointment?.appointmentStartTime, appointment?.appointmentEndTime) === 1 &&
                    appointment?.alertNotificationCount === 0
                ) {
                    currentAppointments.push(appointment);
                }
            });
            currentAppointments?.length
                ? localStorage.setItem('currentAppointments', JSON.stringify(currentAppointments))
                : localStorage.removeItem('currentAppointments');
        } else {
            localStorage.removeItem('currentAppointments');
        }
    };

    const appointmentRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const mobileAppointmentRefs = useRef<Record<string, HTMLDivElement | null>>({});

    useLayoutEffect(() => {
        const desktopElement = appointmentRefs.current[appointmentId];
        const mobileElement = mobileAppointmentRefs.current[appointmentId];

        const targetElement =
            windowWidth > 767 ? desktopElement : mobileElement;

        const scrollContainer = document.querySelector(
            '.appointment-listing',
        ) as HTMLElement;

        if (targetElement && scrollContainer) {
            const targetPosition =
                targetElement.getBoundingClientRect().top +
                scrollContainer.scrollTop -
                scrollContainer.getBoundingClientRect().top;

            const startPosition = scrollContainer.scrollTop;
            const extraOffset = windowWidth > 767 ? 50 : 0;

            const distance =
                targetPosition - startPosition - extraOffset;

            const duration =
                appointmentData && appointmentData.length > 5
                    ? 1500
                    : 500;

            let start: number | null = null;

            const animation = (currentTime: number) => {
                if (!start) start = currentTime;

                const timeElapsed = currentTime - start;

                const easeInOutQuad = (t: number) =>
                    t < 0.5
                        ? 2 * t * t
                        : 1 - Math.pow(-2 * t + 2, 2) / 2;

                const progress = Math.min(timeElapsed / duration, 1);

                scrollContainer.scrollTop =
                    startPosition +
                    distance * easeInOutQuad(progress);

                if (timeElapsed < duration) {
                    requestAnimationFrame(animation);
                }
            };

            requestAnimationFrame(animation);
        }
    }, [appointmentId, appointmentData, windowWidth]);
    useEffect(() => {
        setAppointmentData(appointments || null);
        // storeAppointments(appointments)
    }, [appointments]);

    useEffect(() => {
        if (!!doorOpenData && !!doorOpenData.message && !doorOpenError) {
            setShowToast(true);
            setToastMessage(doorOpenData.message);
            setToastType('success');

            setTimeout(() => {
                dispatch(resetAmazonDoor());
            }, 4000);
        } else if (!!doorOpenError) {
            setShowToast(true);
            setToastMessage(doorOpenError);
            setToastType('error');

            setTimeout(() => {
                dispatch(resetAmazonDoor());
            }, 4000);
        }
    }, [doorOpenData, doorOpenError]);

    useEffect(() => {
        const rawAppointmentId = searchParams.get('appointmentId') || '';
        const [queryAppointmentId, actionFromAppointmentId] = rawAppointmentId.split('?action=');
        const queryAction = (searchParams.get('action') || actionFromAppointmentId || '').toLowerCase();

        setAppointmentId(queryAppointmentId);

        if (queryAction === 'reschedule' || queryAction === 'reschedual' || queryAction === 'reschual') {
            setAppointmentAction('reschedule');
        } else if (queryAction === 'cancel') {
            setAppointmentAction('cancel');
        } else {
            setAppointmentAction('');
        }
    }, [searchParams]);

    useEffect(() => {
        dispatch(getUserAppointments(active === 'Asc' ? 'Asc' : 'Desc'));
    }, [active, dispatch]);

    const handleDelete = () => {
        dispatch(deleteUserAppointment(id, active === 'Asc' ? 'Asc' : 'Desc', { isModified: false }));
        setOpen(false);
    };

    const openModel = (appointmentId: string) => {
        setId(appointmentId);
        setOpen(true);
    };

    const sortHandler = () => {
        active === 'Asc' ? setActive('Desc') : setActive('Asc');
    };

    const navigateForModifyAppointmentHandler = (appointment: AppointmentsProps) => {
        const { locationId, unitId, appointmentDate, appointmentStartTime, appointmentId, appointmentEndTime } = appointment;

        const modifiedAppointmentStartTime = moment(appointmentStartTime, 'HH:mm A').format('HHmm');
        const modifiedAppointmentEndTime = moment(appointmentEndTime, 'HH:mm A').format('HHmm');

        const bookedDate = moment(appointmentDate).format('YYYY-MM-DD');

        navigate(`/location/${locationId}/unit/${unitId}?oldBookedDate=${bookedDate}&oldAppointmentStartTime=${modifiedAppointmentStartTime}&oldAppointmentEndTime=${modifiedAppointmentEndTime}`, {
            state: { appointmentId, active },
        });
    };

    const hasQueryActionForAppointment = (currentAppointmentId: string) => {
        return appointmentAction !== '' && appointmentId === currentAppointmentId;
    };

    const shouldShowEditAction = (currentAppointmentId: string) => {
        return !hasQueryActionForAppointment(currentAppointmentId) || appointmentAction === 'reschedule';
    };

    const shouldShowMapAction = (currentAppointmentId: string) => {
        return !hasQueryActionForAppointment(currentAppointmentId);
    };

    // Hide Cancel once the kiosk has been used (tour started). Visitors were
    // pressing Cancel to "end" a tour, which deleted a completed appointment.
    const shouldShowDeleteAction = (currentAppointmentId: string, entryExitCount: number = 0) => {
        if (entryExitCount > 0) return false;
        return !hasQueryActionForAppointment(currentAppointmentId) || appointmentAction === 'cancel';
    };

    const getEditActionText = () => {
        return 'Reschedule';
    };

    const getDeleteActionText = () => {
        return 'Cancel';
    };

    const [showWebCam, setShowWebCam] = useState(false);
    const [newProfile, setNewProfile] = useState('');
    const [hasMediaAccess, setHasMediaAccess] = useState<boolean | null>(null);
    const [toastMessage, setToastMessage] = useState('');
    const webcamRef = useRef<Webcam>(null);

    const videoConstraints: MediaTrackConstraints | boolean = {
        facingMode: 'user', // or 'environment' for rear camera
    };

    const capturePhoto = () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setNewProfile(imageSrc);
        }
    };

    const base64ToFile = (base64String: string, fileName: string, contentType: string): File => {
        const trimmedBase64String = base64String.trim();

        if (!trimmedBase64String.startsWith('data:image/jpeg;base64,')) {
            throw new Error('Invalid base64 string format');
        }
        const byteCharacters = atob(trimmedBase64String.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        return new File([blob], fileName, { type: contentType });
    };

    const requestCameraPermission = () => {
        const askCameraPermission = async (): Promise<MediaStream | null> => await navigator.mediaDevices.getUserMedia({ video: true });

        var localStream: MediaStream | null;
        askCameraPermission()
            .then(() => { })
            .then(() => {
                setHasMediaAccess(true);
                localStream?.getTracks().forEach((track) => {
                    track.stop();
                });
                setShowWebCam(true);
            })
            .catch((error) => {
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    setHasMediaAccess(false);
                    setToastMessage('Camera access denied! Please grant access to your camera to continue.');
                }
            });
    };

    useEffect(() => {
        if (toastMessage) {
            setTimeout(() => {
                setToastMessage('');
                setShowToast(false);
            }, 4000);
        }
    }, [toastMessage]);

    const onSuccess = (accessCode: string) => {
        setIsLoading(false);
        setShowModalPopup(true);
        setAccessCode(accessCode);
        dispatch(getUserAppointments(active === 'Asc' ? 'Asc' : 'Desc'));
    };

    const onFailure = (errorMsg: string) => {
        setToastMessage(errorMsg);
        setShowToast(true);
        setIsLoading(false);
    };

    const submitPhoto = async () => {
        setIsLoading(true);
        const newUserProfile = newProfile;
        setNewProfile('');
        setShowWebCam(false);

        const file = await base64ToFile(newUserProfile, 'user', 'image/jpeg');
        const appointmentObj: AccessCodeProps = {
            file: file,
            appointmentId: selectedAppointment.appointmentId,
        };

        dispatch(requestAccessCode(appointmentObj, onSuccess, onFailure));
    };

    const onRequestAccessKey = (count: number, appointmentId: string) => {
        setSelectedAppointment({
            appointmentId: appointmentId,
            count: count,
        });
        if (hasMediaAccess === null) {
            requestCameraPermission();
        } else if (hasMediaAccess) {
            setShowWebCam(true);
        } else {
            setShowToast(true);
            setToastMessage('Camera access denied! Please grant access to your camera to continue.');
        }
    };

    const AccessModalBody = () => {
        return (
            <div className={styles.accessModalBodyContainer}>
                <img src={CloseIcon} alt="close-icon" className={styles.closeIconContainer} onClick={() => setShowModalPopup(false)} />
                <div className={styles.successIcon}>
                    <SuccessAnimation />
                </div>
                <p className={styles.successMsg}>Done! We’ve captured and verified your pictured</p>
                <p className={styles.code}>{`Your Access code is : ${accessCode}`}</p>
            </div>
        );
    };

    useEffect(() => {
        if (showModalPopup || showWebCam) {
            window.document.body.style.overflow = 'hidden';
        }

        return () => {
            window.document.body.style.overflow = 'auto';
        };
    }, [showModalPopup, showWebCam]);

    const onRequestReturnAccessKey = (appointmentId: string) => {
        navigate(`/tour-completion/${appointmentId}`);
    };

    const generateRandomBG = () => {
        return colors[Math.floor(Math.random() * colors.length)];
    };

    useEffect(() => {
        if (windowWidth > 767 && showSwipeModal) {
            setShowSwipeModal(false);
        }
    }, [windowWidth]);

    const onOpenVia = (slug: string) => {
        window.open(`https://app.openvia.co/credentials/${slug}`, '_blank', 'noopener,noreferrer');
    };

    const onOpenAmazonDoorSuccess = () => {
        dispatch(getUserAppointments(active === 'Asc' ? 'Asc' : 'Desc'));
    };

    const onOpenAmazonDoor = (appointmentObj: amazonDoorProps) => {
        dispatch(openAmazonDoor(appointmentObj, onOpenAmazonDoorSuccess));
    };

    const onOutSideAlertSuccess = (message: string) => {
        setShowToast(true);
        setToastMessage(message);
        setToastType('success');
        dispatch(getUserAppointments(active === 'Asc' ? 'Asc' : 'Desc'));
    };

    function openLocationSettings() {
        setShowCustomModal(true);
    }

    const ModalBody = () => {
        return (
            <div className={styles.modalBodyContainer}>
                <div className={styles.modalHeader}>
                    <h3>Upload Selfie</h3>

                    <div
                        className={styles.closeIcon}
                        onClick={() => {
                            setShowWebCam(false);
                            setNewProfile('');
                        }}>
                        <img src={CloseIcon} alt="close-icon" width={20} height={20} />
                    </div>
                </div>
                <div className={styles.modalBodyInnerContainer}>
                    {!newProfile && (
                        <>
                            <div className={styles.camLayer}>
                                <img src={CamLayer} alt="layer" width={684} height={480} />
                            </div>
                            <Webcam
                                audio={false}
                                className={styles.webCamContainer}
                                screenshotFormat="image/jpeg"
                                videoConstraints={videoConstraints}
                                ref={webcamRef}
                                autoFocus
                            />
                        </>
                    )}

                    {newProfile && (
                        <div className={styles.capturedImage}>
                            <img src={newProfile} alt="" width={640} height={480} />
                        </div>
                    )}
                </div>
                <div className={styles.modalFooter}>
                    {newProfile && (
                        <button className={styles.retakeBtn} onClick={() => setNewProfile('')}>
                            Recapture Photo
                        </button>
                    )}
                    <button className={newProfile ? styles.submitBtn : styles.captureBtn} onClick={newProfile ? submitPhoto : capturePhoto}>
                        {!!newProfile ? 'Submit my picture' : 'Capture photo'}
                    </button>
                </div>
            </div>
        );
    };

    const getPlatform = (): 'android' | 'ios' | 'default' => {
        const userAgentData = (navigator as any).userAgentData;

        // Check modern userAgentData if available
        if (userAgentData?.platform) {
            const platform = userAgentData.platform.toLowerCase();
            if (platform.includes('android')) return 'android';
            if (platform.includes('iphone') || platform.includes('ipad') || platform.includes('ios')) return 'ios';
        }

        // Fallback to userAgent (works for most browsers)
        const userAgent = navigator.userAgent.toLowerCase();
        if (/android/.test(userAgent)) return 'android';
        if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';

        // Additional check for iPadOS, but only if OS is Mac AND browser is Safari (to avoid Ubuntu false positive)
        if (
            navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints > 1 &&
            /safari/.test(userAgent) &&
            !/chrome|firefox|edg|opr/.test(userAgent) // Ensure it's not Ubuntu
        ) {
            return 'ios';
        }

        return 'default'; // If nothing matches
    };

    const getBrowserName = (): string => {
        const userAgent = navigator.userAgent.toLowerCase();
        const userAgentData = (navigator as any).userAgentData;

        // iOS Browsers
        if (userAgent.includes('crios')) return 'Chrome (iOS)';
        if (userAgent.includes('fxios')) return 'Firefox (iOS)';
        if (userAgent.includes('edgios')) return 'Edge (iOS)';

        // Chromium-based browsers
        if (userAgent.includes('duckduckgo')) return 'DuckDuckGo';
        if (userAgent.includes('vivaldi')) return 'Vivaldi';
        if (userAgent.includes('samsungbrowser')) return 'Samsung Internet';
        if (userAgent.includes('yabrowser')) return 'Yandex Browser';
        if (userAgent.includes('ucbrowser')) return 'UC Browser';

        // More reliable Brave detection
        const isBrave =
            (navigator as any).brave?.isBrave || // Check if Brave explicitly exposes itself
            userAgentData?.brands?.some((brand: { brand: string }) => brand.brand.toLowerCase() === 'brave'); // Check using userAgentData

        if (isBrave) return 'Brave';

        // Standard Browsers
        if (userAgent.includes('opr') || userAgent.includes('opera')) return 'Opera';
        if (userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr')) return 'Chrome';
        if (userAgent.includes('firefox')) return 'Firefox';
        if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'Safari';
        if (userAgent.includes('edg')) return 'Edge';
        if (userAgent.includes('trident') || userAgent.includes('msie')) return 'Internet Explorer';

        return 'Unknown';
    };

    const CustomModalBody = () => {
        // const platform: "android" | "ios" | "default" = getPlatform();
        const platform: 'android' | 'ios' | 'default' = getPlatform();
        const browser: string = getBrowserName();

        const instructions: Record<string, { message: string }> = {
            android: {
                message: `To enable location:\n<span>1️⃣ Open ${browser}.</span><span>2️⃣ Go to Settings > Site Settings.</span><span>3️⃣ Enable location for this website.</span>`,
            },
            ios: {
                message: `To enable location:\n<span>1️⃣ Open Settings.</span><span>2️⃣ Go to Privacy & Security > Location Services.</span><span>3️⃣ Enable location for ${browser}.</span>`,
            },
            default: {
                message: 'Please enable location permissions in your browser settings.',
            },
        };

        const { message } = instructions[platform] || instructions.default;

        return (
            <div className={styles.modalBodyContainer}>
                <div className={`${styles.modalHeader} allowSVG`}>
                    <h3 className={styles.modalH3}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 2C7.6 2 4 5.6 4 10C4 15.4 11 21.5 11.3 21.8C11.5 21.9 11.8 22 12 22C12.2 22 12.5 21.9 12.7 21.8C13 21.5 20 15.4 20 10C20 5.6 16.4 2 12 2ZM12 19.7C9.9 17.7 6 13.4 6 10C6 6.7 8.7 4 12 4C15.3 4 18 6.7 18 10C18 13.3 14.1 17.7 12 19.7ZM12 6C9.8 6 8 7.8 8 10C8 12.2 9.8 14 12 14C14.2 14 16 12.2 16 10C16 7.8 14.2 6 12 6ZM12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12Z"
                                fill="black"
                            />
                        </svg>
                        <span>Enable Location Access</span>
                    </h3>
                </div>
                <div className={styles.modalBodyInnerContainer}>
                    <p className={styles.preWrappedMessage} dangerouslySetInnerHTML={{ __html: message }}></p>
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.captureBtn} onClick={() => setShowCustomModal(false)}>
                        Okay
                    </button>
                </div>
            </div>
        );
    };

    const updateEntryExitCount = (appointmentId: string, entryExitCount: number) => {
        setAppointmentData((prev) =>
            prev
                ? prev.map((appointment) =>
                    appointment.appointmentId === appointmentId ? { ...appointment, entryExitCount } : appointment,
                )
                : prev,
        );
    };

    return (
        <div className={styles.container}>
            {open && (
                <Popup open={open} setOpen={setOpen} bodyData={<DeleteAppointmentPopup setOpen={setOpen} handleDelete={handleDelete} />} />
            )}
            {showBookingInfo && (
                <Popup
                    open={showBookingInfo}
                    setOpen={setShowBookingInfo}
                    bodyData={<TourBookingMessage onClose={() => setShowBookingInfo(false)} />}
                />
            )}
            {(loading || isLoading || doorOpenloading) && <Loader />}
            <div className={styles.appointmentTitle}>
                <h1>Appointments</h1>
                {/* {!loading && appointments && appointments?.length > 0 && (
                    <a className={styles.btnGreen} href="/locations">
                        Schedule
                    </a>
                )} */}
            </div>

            {!loading && <h3 className={styles.existingApointments}>Existing Appointments</h3>}
            {/* Appointments Listing */}
            {!loading && appointments?.length > 0 && (
                <div className={`${styles.appointments} appointment-listing`}>
                    <table className={styles.appointmentTable}>
                        <tbody>
                            {windowWidth > 767 && (
                                <tr className={styles.tableHeader}>
                                    <th>Location name</th>
                                    <th>Unit Name</th>
                                    <th>
                                        <span>
                                            Date & Time <img onClick={sortHandler} src={iconSort} alt="Sort" />
                                        </span>
                                    </th>
                                    <th className={styles.appointmentAction}>Open Via</th>
                                    <th className={styles.appointmentDate}>Access Code</th>
                                    <th className={styles.appointmentAction}></th>

                                    {/* <th className={styles.appointmentAction}>Cancel</th> */}
                                </tr>
                            )}
                            {appointments.length === 0 && (
                                <tr>
                                    <td className={styles.noData} colSpan={6}>
                                        No appointment data found
                                    </td>
                                </tr>
                            )}
                            {appointmentData && appointmentData.length > 0 && windowWidth > 767
                                ? appointmentData.map((appointment: AppointmentsProps) => {
                                    const appointmentStatus = isAccessible(
                                        appointment?.appointmentDate,
                                        appointment?.appointmentStartTime,
                                        appointment?.appointmentEndTime,
                                    );
                                    return (
                                        <tr
                                            ref={(el) => {
                                                appointmentRefs.current[appointment.appointmentId] = el;
                                            }}
                                            className={appointmentId === appointment.appointmentId ? styles.modifiedAppointment : ''}
                                            key={appointment.appointmentId}>
                                            <td
                                                className={styles.Ul}
                                                onClick={() =>
                                                    window.open(`/location/${appointment.locationId}`, '_blank', 'noreferrer')
                                                }>
                                                {appointment.locationName}
                                            </td>
                                            <td>{appointment.unitYardiId ? appointment.unitYardiId : appointment.unitName}</td>
                                            <td>
                                                {moment(appointment.appointmentDate).format('MM-DD-YYYY')}
                                                {' \n'}
                                                {moment(appointment.appointmentStartTime, 'hh:mm A').format('h:mm A')} -{' '}
                                                {moment(appointment.appointmentEndTime, 'hh:mm A').format('h:mm A')}
                                            </td>
                                            <td
                                                className={`${styles.appointmentAction} ${appointment?.openViaSlug && appointmentStatus !== -1 ? '' : styles.disabled
                                                    }`}
                                                onClick={() => {
                                                    if (appointment?.openViaSlug && appointmentStatus !== -1) {
                                                        onOpenVia(appointment?.openViaSlug);
                                                    }
                                                }}>
                                                Open Via
                                            </td>
                                            <td>{appointment?.accessCode || ''}</td>
                                            <td>
                                                <div className={styles.actionButtonRow}>
                                                    {shouldShowEditAction(appointment.appointmentId) && (
                                                        <button
                                                            type="button"
                                                            className={`${styles.tableActionButton} ${styles.editActionButton} ${appointmentStatus === -1 ? styles.disabled : ''}`}
                                                            disabled={appointmentStatus === -1}
                                                            onClick={() => navigateForModifyAppointmentHandler(appointment)}>
                                                            <EditIcon fill="#fff" />
                                                            <span>{getEditActionText()}</span>
                                                        </button>
                                                    )}
                                                    {shouldShowMapAction(appointment.appointmentId) && (
                                                        <button
                                                            type="button"
                                                            className={`${styles.tableActionButton} ${styles.mapActionButton}`}
                                                            onClick={() => navigate(`/map-view/${appointment?.appointmentId}`)}>
                                                            <MapIcon stroke="#fff" />
                                                            <span>Map</span>
                                                        </button>
                                                    )}
                                                    {shouldShowDeleteAction(appointment.appointmentId, appointment.entryExitCount) && (
                                                        <button
                                                            type="button"
                                                            className={`${styles.tableActionButton} ${styles.deleteActionButton}`}
                                                            onClick={() => openModel(appointment.appointmentId)}>
                                                            <TrashIcon fill="#fff" />
                                                            <span>{getDeleteActionText()}</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                                : null}
                        </tbody>
                    </table>

                    {appointmentData && appointmentData.length > 0 && windowWidth < 767 && (
                        <div className={styles.appointmentCardListing}>
                            {appointmentData &&
                                appointmentData?.map((appointment: AppointmentsProps, index: number) => {
                                    return (
                                        <div
                                            key={index}
                                            ref={(el) => {
                                                mobileAppointmentRefs.current[appointment.appointmentId] = el;
                                            }}
                                            className={
                                                appointmentId === appointment.appointmentId
                                                    ? styles.modifiedAppointmentCard
                                                    : ''
                                            }>

                                            <AppointmentCard
                                                key={index}
                                                openLocationSettings={openLocationSettings}
                                                appointment={appointment}
                                                loading={doorOpenloading}
                                                updateEntryExitCount={updateEntryExitCount}
                                                onAmazonDoor={() => {
                                                    if (
                                                        isSupported &&
                                                        isEnabled &&
                                                        !geoLocationError &&
                                                        coordinates?.latitude &&
                                                        coordinates?.longitude
                                                    ) {
                                                        const appointmentObj: amazonDoorProps = {
                                                            locationId: appointment.locationId,
                                                            appointmentId: appointment.appointmentId,
                                                            latitude: coordinates.latitude,
                                                            longitude: coordinates.longitude,
                                                            timestamp: Math.floor(Date.now()),
                                                            amazonFlag: AmazoneFlag.Location,
                                                        };
                                                        onOpenAmazonDoor(appointmentObj);
                                                    } else {
                                                        setShowToast(true);
                                                        setToastMessage('Please enable location permissions to unlock the door.');
                                                        setToastType('error');
                                                    }
                                                }}
                                                onEditAppointment={() => navigateForModifyAppointmentHandler(appointment)}
                                                onDeleteAppointment={() => openModel(appointment.appointmentId)}
                                                showEditAppointment={shouldShowEditAction(appointment.appointmentId)}
                                                showDeleteAppointment={shouldShowDeleteAction(appointment.appointmentId, appointment.entryExitCount)}
                                                editAppointmentText={`${getEditActionText()}`}
                                                deleteAppointmentText={`${getDeleteActionText()}`}
                                                background={generateRandomBG()}
                                                onMenuClick={() => {
                                                    setShowSwipeModal(true);
                                                    setSelectedTour(appointment);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                        </div>
                    )}

                    {!!showSwipeModal && (
                        <div
                            className={styles.backdrop}
                            onClick={() => {
                                setShowSwipeModal(false);
                                setSelectedTour(null);
                            }}></div>
                    )}
                    {/* <div className={`${styles.swipeModal} ${showSwipeModal ? styles.active : styles.inactive}`}>
                        <p>I want to </p>
                        {selectedTour &&
                            isAccessible(
                                selectedTour?.appointmentDate,
                                selectedTour?.appointmentStartTime,
                                selectedTour?.appointmentEndTime,
                            ) !== -1 && (
                                <button
                                    className={`${styles.btn}`}
                                    onClick={() => {
                                        if (
                                            selectedTour &&
                                            isAccessible(
                                                selectedTour?.appointmentDate,
                                                selectedTour?.appointmentStartTime,
                                                selectedTour?.appointmentEndTime,
                                            ) !== -1
                                        ) {
                                            navigateForModifyAppointmentHandler(selectedTour);
                                            setShowSwipeModal(false);
                                        }
                                    }}>
                                    Edit This Appointment
                                </button>
                            )}
                        <button
                            className={`${styles.btn} ${styles.delete}`}
                            onClick={() => {
                                if (selectedTour) {
                                    openModel(selectedTour.appointmentId);
                                    setShowSwipeModal(false);
                                }
                            }}>
                            Delete This Appointment
                        </button>
                        <button
                            className={`${styles.btn} ${styles.cancel}`}
                            onClick={() => {
                                setShowSwipeModal(false);
                                setSelectedTour(null);
                            }}>
                            Cancel
                        </button>
                    </div> */}
                </div>
            )}

            {/* No appointments */}
            {!loading && appointments?.length === 0 && (
                <div className={`${styles.noAppointmentContainer} appointment-listing`}>
                    <img src={NoAppointments} alt="no-appointment" width={132} height={260} className={styles.noAppointmentImg} />
                    <p
                        className={
                            styles.noAppointments
                        }>{`It looks like you haven’t booked any appointments yet.\nLet’s get you started! Click the button below to book your appointment and find your dream Space!`}</p>
                    <button type="button" className={styles.btnGreen} onClick={() => setShowBookingInfo(true)}>
                        Book An Appointment
                    </button>
                </div>
            )}

            {showWebCam && <Popup open={showWebCam} setOpen={setShowWebCam} bodyData={<ModalBody />} className="custom-webcam-modal" />}
            {!!showModalPopup && (
                <Popup
                    open={showModalPopup}
                    setOpen={setShowModalPopup}
                    onCloseModal={() => setShowModalPopup(false)}
                    bodyData={<AccessModalBody />}
                    className="custom-webcam-modal"
                    shouldCloseOnOverlayClick={true}
                />
            )}
            {showCustomModal && (
                <Popup setOpen={setShowCustomModal} className="custom-webcam-modal" open={showCustomModal} bodyData={<CustomModalBody />} />
            )}
            {showToast && <Toast message={toastMessage} type={toastType || 'error'} />}
        </div>
    );
};

export default Appointments;
